// cSpell:ignore pand refetches
// Mirrors projects/rentio-dashboard/src/scenes/Properties/scenes/Contracts/scenes/ContractInfo/ContractInfo.tsx
// Page wrapper with title, subtitle, breadcrumbs around the settlement wizard.

import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import { EPropertyTypeId } from "@rentiohq/shared-frontend/dist/types/property.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Error, Loading, Page } from "@rentiohq/web-shared/dist/components";
import React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { StringParam } from "serialize-query-params";
import { MissingDetail } from "../../components/MissingDetail";
import { mustLeaveSettlementWizard } from "../../costsEditAccess.utils";
import { useCostsEditPermission, useSettlement } from "../../hooks";
import { getMissingDetailState } from "../../missingDetail.utils";
import { SettlementForm } from "./SettlementForm/SettlementForm";

export const SettlementInfo: React.FC = () => {
  const { propertyId, settlementId } = useParams<{
    propertyId: string;
    settlementId?: string;
  }>();
  const pidNum = propertyId ? +propertyId : undefined;
  const navigate = useNavigate();
  // New mode: the /add route has no :settlementId and the wizard should render
  // from a local draft only. A backend row is created lazily on the first real
  // step transition.
  const isNew = !settlementId;
  // A departure settlement for this lease (see ISettledDeparture), asked from
  // a unit's costs tab.
  const [{ departure: departureContractId }] = useQueryParams({
    departure: StringParam,
  });
  const { canEdit, isLoading: isCheckingEditAccess } =
    useCostsEditPermission(propertyId);

  const {
    detail: property,
    isFetching: isFetchingProperty,
    fetchError: propertyFetchError,
  } = propertyHooks.useDetail({ shouldRefetch: false, id: pidNum });

  const {
    settlement,
    isFetching: isFetchingSettlement,
    fetchError: settlementFetchError,
    refetch: refetchSettlement,
  } = useSettlement(settlementId);

  // Stable identity: an inline object here would re-create the wizard's
  // virtual settlement (and every filter derived from it) on each render.
  const virtualScope = React.useMemo(() => {
    if (!isNew) return undefined;
    const isGroup = property?.typeId === EPropertyTypeId.Group;
    return {
      scope: (isGroup ? "group" : "unit") as "group" | "unit",
      groupId: isGroup ? propertyId : undefined,
      unitId: isGroup ? undefined : propertyId,
      departureContractId: isGroup
        ? undefined
        : departureContractId ?? undefined,
    };
  }, [isNew, property?.typeId, propertyId, departureContractId]);

  // The wizard keeps its step + draft in local state, so it must never be
  // unmounted once it's up. Background refetches (a payment order created in
  // the embedded payment flow invalidates the property/settlement detail) would
  // otherwise flip us back to <Loading/> and restart the flow at step 1.
  // Hold on to the last known-good data and only gate on the initial load.
  const lastProperty = React.useRef(property);
  if (property) lastProperty.current = property;
  const stableProperty = property ?? lastProperty.current;

  const lastSettlement = React.useRef(settlement);
  if (settlement) lastSettlement.current = settlement;
  const stableSettlement = settlement ?? lastSettlement.current;

  const hasRendered = React.useRef(false);

  if (!hasRendered.current) {
    // A landlord's edit access is asked from the API: until it answers, the
    // "no" below is only "not yet", and acting on it sent them away.
    if (
      isFetchingProperty ||
      (settlementId && isFetchingSettlement) ||
      isCheckingEditAccess
    )
      return <Loading />;
    if (propertyFetchError) return <Error errors={[propertyFetchError]} />;
  }
  if (!stableProperty) return null;

  const heading = getLocalizedText("settlement.page_title");
  const backTo = `/properties/${propertyId}/costs`;
  const breadcrumbs = {
    to: backTo,
    content: getLocalizedText("costs.back_to_costs"),
  };

  // A link to a settlement that is gone (deleted) or not the viewer's says so
  // instead of a blank page (X05).
  if (!isNew && !stableSettlement) {
    const missingState = getMissingDetailState({
      isFetching: isFetchingSettlement,
      fetchError: settlementFetchError,
    });
    if (missingState === "loading") return <Loading />;
    return (
      <Page title={heading} breadcrumbs={breadcrumbs}>
        <MissingDetail
          kind="settlement"
          state={missingState}
          onClose={() => navigate(backTo)}
          closeLabel={getLocalizedText("costs.back_to_costs")}
          onRetry={refetchSettlement}
        />
      </Page>
    );
  }
  hasRendered.current = true;

  // An archived property is only read until it is activated again: no new
  // settlement, and a confirmed one without its actions, like its costs.
  const isArchived = !!stableProperty.archivedAt;

  // Making or finishing a settlement is a write: a user who may only read is
  // sent back to the costs (a confirmed settlement stays readable).
  if (
    mustLeaveSettlementWizard({
      isPropertyArchived: isArchived,
      canEdit,
      isCheckingEditAccess,
      isNew,
      isConfirmed: stableSettlement?.status === "confirmed",
    })
  ) {
    return <Navigate to={backTo} replace />;
  }

  return (
    <Page
      title={heading}
      // Which pand or group this settlement is for.
      subtitle={formatAddress(stableProperty, false) || stableProperty.name}
      breadcrumbs={breadcrumbs}
    >
      <SettlementForm
        settlementId={stableSettlement?.id}
        property={stableProperty}
        virtualScope={virtualScope}
        readOnly={isArchived}
      />
    </Page>
  );
};
